import { defineComponent as _defineComponent } from 'vue'
import { checkUserPermissions } from '@/auth/user_function'
import { USER_PERMISSION_KEY } from '@/data/auth'
import store from '@/store'
import { getSawKnife } from '@/util/LayoutFuncs'
import {
  dealExpandSlotForPaiban,
  isOnlyProcessSlotAndHole,
} from '@/util/LayoutTool'
import { matchHighlightBoardName } from '@/util/commonFun'
import { modalMsg } from '@/util/commonFuncs'
import { paibanDataToView } from '@/util/dealPaibanData'
import { isNeedFanByBigPlank } from '@/views/newPaiban/util/layoutDataDeal'
import { Loading, Message } from 'element-ui'
import { computed, onMounted, ref, watch } from 'vue'

import {
  MDeepBadgeAurora,
  MDeepBadgeCrimson,
  MDeepBadgeGold,
  MDeepBadgeJade,
  MDeepBadgeOutline,
  MDeepBadgePulse,
} from '../iconComponents'
import type { ICollectType, IRate, ITextureReturnType } from './types'


export default /*#__PURE__*/_defineComponent({
  __name: 'alignedPlan',
  props: {
    alignedPlanDialog: { type: Boolean, default: false },
    currentFanbanCount: { default: undefined }
  },
  emits: [
  'useApply',
  'setAlignedPlanDialog',
  'openDisparsePaibanCOnfirm',
  'onChangeShowDeepTabTip',
],
  setup(__props: any, { expose, emit }) {

const props = __props as { alignedPlanDialog: boolean, currentFanbanCount: number }





/** 尊享版走分材质；非尊享走组合方案整套列表 */
const isExpressVip = computed(() => Boolean(window.webToQt?.expressVip))

const currentLine = computed(() => {
  return store.state.proLineList.find(
    (item: any) => item.id === store.state.ncSetting.process_setting_id
  )
})

function getBigPlankParts(bigPlank: any): any[] {
  if (!bigPlank) return []
  if (Array.isArray(bigPlank.parts)) return bigPlank.parts
  if (bigPlank.parts && typeof bigPlank.parts === 'object') {
    return Object.values(bigPlank.parts)
  }
  return []
}

/** 指定材质不切割大板不计入翻板（与 processInitDrawData 一致） */
function isDesMaterialNoCutBigPlank(bigPlank: any): boolean {
  if (bigPlank?.isDesMaterialNoCut) return true
  return getBigPlankParts(bigPlank).some((p) => p?.isDesMaterialNoCut)
}

/**
 * 与激活后 processInitDrawData / genFinalDataOrder 一致：
 * 用 isNeedFanByBigPlank，排除指定材质不切割。
 */
function countFanbanLikeActivate(bigPlankList: any[], nc: any): number {
  if (!Array.isArray(bigPlankList) || !bigPlankList.length) return 0
  let count = 0
  for (const bigPlank of bigPlankList) {
    if (isDesMaterialNoCutBigPlank(bigPlank)) continue
    const parts = getBigPlankParts(bigPlank)
    if (!parts.length) continue
    try {
      if (isNeedFanByBigPlank({ ...bigPlank, parts }, nc)) {
        count++
      }
    } catch (e) {
      // 单板字段异常时跳过
    }
  }
  return count
}

/** 预排版小板索引，激活时 afterProcess 同源数据 */
function getPrePartMap(): Map<string, any> {
  const list =
    store.state.prePaibanData || store.state.paibanStore?.prePaibanData || []
  const arr = Array.isArray(list) ? list : Object.values(list || {})
  const map = new Map<string, any>()
  arr.flat(Infinity).forEach((p: any) => {
    if (!p || typeof p !== 'object') return
    if (p.partUniqueId != null) map.set(String(p.partUniqueId), p)
    if (p.id != null) map.set(String(p.id), p)
    if (p.ggid != null) map.set(`ggid:${p.ggid}`, p)
  })
  return map
}

/** 用预排版孔槽补齐，使未激活方案与激活后 isNeedFanByBigPlank 一致 */
function enrichPartForFanban(part: any, preMap: Map<string, any>): any {
  if (!part || !preMap.size) return part
  const src =
    (part.id != null && preMap.get(String(part.id))) ||
    (part.partUniqueId != null && preMap.get(String(part.partUniqueId))) ||
    (part.ggid != null && preMap.get(`ggid:${part.ggid}`)) ||
    null
  if (!src) return part
  return {
    ...part,
    holes: src.holes ?? part.holes ?? [],
    slots: src.slots ?? part.slots ?? [],
    curveHoles: src.curveHoles ?? part.curveHoles ?? [],
    millInfo: src.millInfo ?? part.millInfo ?? [],
    handleSlopes: src.handleSlopes ?? part.handleSlopes ?? [],
    lineSlots: src.lineSlots ?? part.lineSlots ?? [],
    thick: part.thick ?? src.thick,
    specialType: part.specialType ?? src.specialType,
    isDesMaterialNoCut: part.isDesMaterialNoCut ?? src.isDesMaterialNoCut,
    process_in_engraving_to_curve:
      src.process_in_engraving_to_curve ?? part.process_in_engraving_to_curve,
    engraving_cut_curve: src.engraving_cut_curve ?? part.engraving_cut_curve,
    engraving_cut_curve_to_turn:
      src.engraving_cut_curve_to_turn ?? part.engraving_cut_curve_to_turn,
  }
}

/**
 * 原始方案 cutItem.data[0] 多为小板数组，按 stockNum 聚成大板；
 * 并用 prePaibanData 补齐孔槽（对齐激活时 afterProcess）。
 */
function extractBigPlanksFromCutItem(
  cutItem: any,
  stockKey: string,
  preMap?: Map<string, any>
): any[] {
  const raw = cutItem?.data
  if (!raw) return []
  const planLevel = Array.isArray(raw) ? raw[0] : raw
  if (!planLevel) return []

  const keyArr = String(stockKey).split(':')
  const texture = keyArr[0]
  const matCode = keyArr[1]
  const thickFromKey = Number(keyArr[2])
  const mapRef = preMap || new Map()

  function normalizeBigPlank(bp: any) {
    const parts = getBigPlankParts(bp).map((p) =>
      enrichPartForFanban(p, mapRef)
    )
    return {
      ...bp,
      thick: bp.thick ?? thickFromKey,
      matCode: bp.matCode ?? matCode,
      texture: bp.texture ?? texture,
      parts,
    }
  }

  // 大板对象映射 { stockKey: bigPlank }
  if (!Array.isArray(planLevel) && typeof planLevel === 'object') {
    const values = Object.values(planLevel) as any[]
    if (values.some((v) => v && (v.parts || getBigPlankParts(v).length))) {
      return values
        .filter((v) => v && typeof v === 'object')
        .map(normalizeBigPlank)
        .filter((bp) => bp.parts.length)
    }
    return []
  }

  if (!Array.isArray(planLevel)) return []

  // 已是大板数组
  if (
    planLevel.some(
      (item) => item && (item.parts || getBigPlankParts(item).length)
    )
  ) {
    return planLevel
      .filter((bp) => bp && typeof bp === 'object')
      .map(normalizeBigPlank)
      .filter((bp) => bp.parts.length)
  }

  // 小板数组：按 stockNum 聚合（与 separatePlank 一致）
  const map = new Map<string | number, any>()
  planLevel.forEach((part: any) => {
    if (!part || typeof part !== 'object') return
    const sn = part.stockNum
    if (sn === undefined || sn === null) return
    const enriched = enrichPartForFanban(part, mapRef)
    if (!map.has(sn)) {
      map.set(sn, {
        thick: enriched.thick ?? thickFromKey,
        matCode: enriched.matCode ?? matCode,
        texture: enriched.texture ?? texture,
        otherPlate: enriched.otherPlate,
        surplusInfo: enriched.surplusInfo,
        isDesMaterialNoCut: !!enriched.isDesMaterialNoCut,
        parts: [] as any[],
      })
    }
    const bp = map.get(sn)
    if (enriched.otherPlate) bp.otherPlate = enriched.otherPlate
    if (enriched.surplusInfo) bp.surplusInfo = enriched.surplusInfo
    if (enriched.isDesMaterialNoCut) bp.isDesMaterialNoCut = true
    bp.parts.push(enriched)
  })
  return Array.from(map.values())
}

/**
 * 列表翻板数 = 激活该方案后页头会显示的值。
 * 当前方案用页头 live；已激活过用缓存 fanbanCount；其余按激活规则现算。
 */
function calcPlanFanbanCount(
  planData: any,
  specialId?: string | number
): number {
  const nc = store.state.ncSetting
  const currentId = store.state.paibanStore?.currentUsingSpecialId
  const isCurrentCombinedPlan =
    !isExpressVip.value &&
    specialId !== undefined &&
    specialId !== null &&
    String(specialId) === String(currentId)

  // 当前方案：与页头完全一致（含激活后用户操作）
  if (
    isCurrentCombinedPlan &&
    props.currentFanbanCount !== undefined &&
    props.currentFanbanCount !== null
  ) {
    return Number(props.currentFanbanCount) || 0
  }

  // 已激活过：优先用激活时写入的 fanbanCount
  if (!isExpressVip.value && specialId !== undefined && specialId !== null) {
    const cached = store.state.paibanStore?.alreadyDealDrawData?.[specialId]
    if (cached && typeof cached.fanbanCount === 'number') {
      return cached.fanbanCount
    }
    if (Array.isArray(cached?.paibanData) && cached.paibanData.length) {
      return countFanbanLikeActivate(cached.paibanData, nc)
    }
  }

  if (isCurrentCombinedPlan && Array.isArray(store.state.paibanData)) {
    return countFanbanLikeActivate(store.state.paibanData, nc)
  }

  if (!planData || typeof planData !== 'object') {
    return 0
  }
  const line = computed(() => {
    return currentLine.value
  })
  const preMap = getPrePartMap()
  let total = 0
  let hasAnyBigPlank = false

  Object.keys(planData).forEach((key) => {
    const cutItem = planData[key]
    if (
      !cutItem ||
      typeof cutItem !== 'object' ||
      !('used_rate' in cutItem) ||
      !cutItem.data
    ) {
      return
    }
    const matCode = key.split(':')[1]
    if (matCode && isOnlyProcessSlotAndHole(line.value, matCode)) {
      return
    }
    const bigPlanks = extractBigPlanksFromCutItem(cutItem, key, preMap)
    if (!bigPlanks.length) return
    hasAnyBigPlank = true
    total += countFanbanLikeActivate(bigPlanks, nc)
  })

  if (hasAnyBigPlank) {
    return total
  }

  return Object.keys(planData).reduce((sum, key) => {
    const matCode = key.split(':')[1]
    if (matCode && isOnlyProcessSlotAndHole(line.value, matCode)) {
      return sum
    }
    const cutItem = planData[key]
    if (!cutItem || typeof cutItem !== 'object' || !('used_rate' in cutItem)) {
      return sum
    }
    const roll = Array.isArray(cutItem.rollCount)
      ? Number(cutItem.rollCount[0]) || 0
      : Number(cutItem.rollCount) || 0
    return sum + roll
  }, 0)
}

// 按材质颜色厚度分类后的排版方案对象 { key: [plan1, plan2, ...] }
const resultPlansObjMap = computed(() => {
  const collect = store.state.paibanStore?.resultPlansObj
  if (!collect || typeof collect !== 'object' || Array.isArray(collect)) {
    return {} as Record<string, any[]>
  }
  return collect as Record<string, any[]>
})

const matKeyList = computed(() => {
  return Object.keys(resultPlansObjMap.value)
})

/** 非尊享：组合方案列表（paibanDataCollect） */
const combinedPlanList = computed<ICollectType[]>(() => {
  if (isExpressVip.value) {
    return []
  }
  const collect = store.state.paibanStore?.paibanDataCollect
  if (!collect?.length) {
    if (store.state.ncSetting.glass_setting) {
      return []
    }
    return []
  }
  const result: ICollectType[] = []
  collect.forEach((it: any) => {
    const data = it.data ?? it
    const specialId = it.specialId
    const isGreatPlan = it.isGreatPlan ?? data.isGreatPlan
    const isDeepSearch = it.isDeepSearch ?? data.isDeepSearch
    const newObj: any = {
      greatPaiban: isGreatPlan,
      specialId,
      isDeepSearch,
    }
    const plankNum: number[] = []
    const rollCount: number[] = []
    const optimizeRate: IRate[] = []
    const stockArr: any[] = []
    Object.keys(data).forEach((key) => {
      const cutItem: ITextureReturnType = data[key]
      if (
        !cutItem ||
        typeof cutItem !== 'object' ||
        !('used_rate' in cutItem)
      ) {
        return
      }
      let rate
      if (Array.isArray(cutItem.used_rate)) {
        rate =
          cutItem.used_rate.reduce((pre: number, cut: any) => {
            if (Array.isArray(cut)) {
              optimizeRate.push({ rate: cut[0].rate })
              return (pre += cut[0].rate)
            }
            optimizeRate.push({ rate: cut.rate })
            return (pre += cut.rate)
          }, 0) / cutItem.used_rate.length
      } else {
        rate = cutItem.used_rate.rate
        optimizeRate.push({ rate })
      }
      const plankData = (cutItem.data ?? []).flat(Infinity)
      const otherPlate = plankData.find(
        (plank: any) => plank.otherPlate
      )?.otherPlate
      stockArr.push({
        stockKey: key,
        rate,
        plankNum: cutItem?.stockCount?.[0] || cutItem.stockCount,
        rollCount: cutItem.rollCount?.[0],
        otherText: otherPlate
          ? `${otherPlate.width}*${otherPlate.height}`
          : null,
      })
      plankNum.push(cutItem?.stockCount?.[0] || (cutItem.stockCount as any))
      rollCount.push(cutItem.rollCount?.[0] || 0)
    })
    stockArr.sort((a, b) => {
      const lightKeyA = a.stockKey.includes('高光')
      const lightKeyB = b.stockKey.includes('高光')
      if (lightKeyA !== lightKeyB) {
        return lightKeyA ? -1 : 1
      }
      return a.stockKey.localeCompare(b.stockKey)
    })
    Object.assign(newObj, {
      overallPlankNum: plankNum.reduce((pre, cut) => pre + cut, 0),
      overallPlankRate: optimizeRate.length
        ? optimizeRate.reduce((pre, cut) => pre + cut.rate, 0) /
          optimizeRate.length
        : 0,
      stock: stockArr,
      // 与页头 calcFanbanCount 同源，避免 rollCount 与 ableToggle 不一致
      rollAllCount: calcPlanFanbanCount(data, specialId),
    })
    result.push(newObj)
  })
  if (!result.length) {
    return result
  }
  const maxItem = result.reduce((acc, cur) =>
    (cur.overallPlankRate * 100).toFixed(2) >
    (acc.overallPlankRate * 100).toFixed(2)
      ? cur
      : acc
  )
  maxItem.isMaxRate = true
  return result
})

const activeMatKey = ref('')
// 已关闭 tip 的材质 key：不在列表内的需要显示 tip
const closeMatKeyList = ref<string[]>([])
const deepTabTipTitle = ref('深度计算给出更省板方案~')

function isPlanDeepSearch(plan: any) {
  return !!(plan?.isDeepSearch || plan?.data?.isDeepSearch)
}

/**
 * 深度方案是否优于当前激活方案（用于 tip 是否提示）
 * 1. 当前已是深度 → false
 * 2. 否则依次比：大板数量(少优) → 优化率(高优) → 翻板数量(少优)；
 *    首个有差异的维度上深度更差则 false，更好则 true；全相同则 false
 */
function hasBetterDeepSearchPlans(
  matKey: string,
  usingMap?: Record<string, string | number>
) {
  const plans = resultPlansObjMap.value[matKey] || []
  if (!plans.length) {
    return false
  }

  const deepPlan = plans.find((plan: any) => isPlanDeepSearch(plan))
  if (!deepPlan) {
    return false
  }

  const map =
    usingMap || store.state.paibanStore?.currentUsingSpecialIdMap || {}
  const activeId =
    map[matKey] ??
    plans[getDefaultPlanIndex(plans)]?.specialId ??
    `${matKey}__0`
  const currentPlan =
    plans.find((item: any, idx: number) => {
      const id = item.specialId ?? `${matKey}__${idx}`
      return String(id) === String(activeId)
    }) ||
    plans[getDefaultPlanIndex(plans)] ||
    plans[0]

  // 当前已是深度计算，无需提示
  if (isPlanDeepSearch(currentPlan)) {
    return false
  }

  const [currentView] = transformPlans(matKey, [currentPlan])
  const [deepView] = transformPlans(matKey, [deepPlan])
  if (!currentView || !deepView) {
    return false
  }

  const currentPlankNum = currentView.overallPlankNum
  const deepPlankNum = deepView.overallPlankNum
  // 大板数量：深度更大则不更优
  if (deepPlankNum !== currentPlankNum) {
    return deepPlankNum < currentPlankNum
  }

  const currentRate = Number((currentView.overallPlankRate * 100).toFixed(2))
  const deepRate = Number((deepView.overallPlankRate * 100).toFixed(2))
  // 优化率：深度更小则不更优
  if (deepRate !== currentRate) {
    return deepRate > currentRate
  }

  const currentRoll = currentView.rollAllCount
  const deepRoll = deepView.rollAllCount
  // 翻板数量：深度更大则不更优
  if (deepRoll !== currentRoll) {
    return deepRoll < currentRoll
  }

  return false
}

function isDeepTabTipVisible(matKey: string) {
  return (
    hasBetterDeepSearchPlans(matKey) &&
    !closeMatKeyList.value.includes(matKey) &&
    props.alignedPlanDialog
  )
}

function handleDeepTabTipVisibleChange(matKey: string, visible: boolean) {
  // 受控模式：已关闭的不再被 hover 打开；关闭只走右上角 x
  if (!visible) {
    return
  }
  if (closeMatKeyList.value.includes(matKey)) {
    return
  }
}

function handleCloseDeepTabTip(matKey?: string) {
  const targetKey = matKey || activeMatKey.value
  if (!targetKey || closeMatKeyList.value.includes(targetKey)) {
    return
  }
  closeMatKeyList.value = [...closeMatKeyList.value, targetKey]
  emitDeepTabTipShowState()
}

function handleOpenDeepTabTip(matKey?: string, title?: string) {
  if (title) {
    deepTabTipTitle.value = title
  }
  if (!matKey) {
    closeMatKeyList.value = []
    emitDeepTabTipShowState()
    return
  }
  closeMatKeyList.value = closeMatKeyList.value.filter((key) => key !== matKey)
  emitDeepTabTipShowState()
}

/** 当前仍应展示 tip 的材质数量（有深度方案且未关闭）；非尊享看组合列表 */
function getDeepTabTipShowCount() {
  if (!isExpressVip.value) {
    const currentId = store.state.paibanStore?.currentUsingSpecialId
    const hasBetterDeep = combinedPlanList.value.some(
      (item) =>
        item.isDeepSearch && String(item.specialId) !== String(currentId)
    )
    return hasBetterDeep ? 1 : 0
  }
  return matKeyList.value.filter(
    (matKey) =>
      hasBetterDeepSearchPlans(matKey) &&
      !closeMatKeyList.value.includes(matKey)
  ).length
}

function emitDeepTabTipShowState() {
  emit('onChangeShowDeepTabTip', getDeepTabTipShowCount())
}

watch(
  closeMatKeyList,
  () => {
    emitDeepTabTipShowState()
  },
  { immediate: true }
)

/**
 * 按各材质是否存在更优深度方案，同步 closeMatKeyList。
 * - 无更优深度方案：加入 close（不提示）
 * - 有更优深度方案：移出 close（需要提示）
 */
function dealMatKeyForCloseList(usingMap?: Record<string, string | number>) {
  const nextClose = new Set<string>(
    closeMatKeyList.value.filter((key) => matKeyList.value.includes(key))
  )

  matKeyList.value.forEach((matKey) => {
    if (hasBetterDeepSearchPlans(matKey, usingMap)) {
      nextClose.delete(matKey)
    } else {
      nextClose.add(matKey)
    }
  })

  closeMatKeyList.value = Array.from(nextClose)
  emitDeepTabTipShowState()
}

expose({
  getDeepTabTipShowCount,
  handleOpenDeepTabTip,
  handleCloseDeepTabTip,
  dealMatKeyForCloseList,
})

// 6个一组的循环取值
const getComponentId = (index: number) => {
  const componentIdList = [
    MDeepBadgeAurora,
    MDeepBadgeCrimson,
    MDeepBadgeGold,
    MDeepBadgeJade,
    MDeepBadgeOutline,
    MDeepBadgePulse,
  ]
  return componentIdList[index % componentIdList.length]
}

watch(
  matKeyList,
  (keys) => {
    if (!keys.length) {
      activeMatKey.value = ''
      closeMatKeyList.value = []
      emitDeepTabTipShowState()
      return
    }
    if (!keys.includes(activeMatKey.value)) {
      activeMatKey.value = keys[0]
    }
    // 清理已不存在的 matKey，并按当前方案同步 tip
    closeMatKeyList.value = closeMatKeyList.value.filter((key) =>
      keys.includes(key)
    )
    dealMatKeyForCloseList()
  },
  { immediate: true }
)

watch(
  () => props.alignedPlanDialog,
  (val) => {
    if (val) {
      // 打开弹窗时按当前激活方案同步 tip 显隐
      dealMatKeyForCloseList()
    }
  }
)

// 当前使用的方案
const cutUsePlan = computed(() => {
  return store.state.paibanStore?.currentUsingSpecialId
})

// 各材质当前使用的方案
const cutUsePlanMap = computed(() => {
  return store.state.paibanStore?.currentUsingSpecialIdMap || {}
})

function isCurrentUsingPlan(item: ICollectType & { matKey?: string }) {
  const matKey = item.matKey || activeMatKey.value
  if (matKey && cutUsePlanMap.value[matKey] !== undefined) {
    return String(cutUsePlanMap.value[matKey]) === String(item.specialId)
  }
  return String(item.specialId) === String(cutUsePlan.value)
}

/**
 * 将单个材质下的原始方案列表转换为可展示数据
 */
function transformPlans(matKey: string, plans: any[]): ICollectType[] {
  if (!plans?.length) {
    return []
  }
  const result: ICollectType[] = []
  plans.forEach((it, planIdx) => {
    // 兼容已包装 { specialId, data } 与原始 plan 两种结构
    const data = it.data ?? it
    const specialId = it.specialId ?? `${matKey}__${planIdx}`
    const isGreatPlan = it.isGreatPlan ?? data.isGreatPlan
    const isDeepSearch = it.isDeepSearch ?? data.isDeepSearch
    const newObj: any = {
      greatPaiban: isGreatPlan,
      specialId,
      isDeepSearch,
      matKey,
      planIdx,
    }
    const plankNum: number[] = []
    const rollCount: number[] = []
    const optimizeRate: IRate[] = []
    const stockArr: any[] = []
    Object.keys(data).forEach((key) => {
      const keyArr = key.split(':')
      const matCode = keyArr[1]
      if (isOnlyProcessSlotAndHole(currentLine.value, matCode)) {
        return
      }
      const cutItem: ITextureReturnType = data[key]
      if (
        !cutItem ||
        typeof cutItem !== 'object' ||
        !('used_rate' in cutItem)
      ) {
        return
      }
      let rate
      if (Array.isArray(cutItem.used_rate)) {
        rate =
          cutItem.used_rate.reduce((pre: number, cut: any) => {
            if (Array.isArray(cut)) {
              optimizeRate.push({ rate: cut[0].rate })
              return (pre += cut[0].rate)
            } else {
              optimizeRate.push({ rate: cut.rate })
              return (pre += cut.rate)
            }
          }, 0) / cutItem.used_rate.length
      } else {
        rate = cutItem.used_rate.rate
        optimizeRate.push({ rate: rate })
      }
      const plankData = (data[key].data ?? []).flat(Infinity)
      const isAllOtherPlank = plankData.every((plank: any) => plank.otherPlate)
      const otherPlate = plankData.find(
        (plank: any) => plank.otherPlate
      )?.otherPlate
      const stock = {
        isAllOtherPlank,
        stockKey: key,
        rate: rate,
        plankNum: cutItem?.stockCount?.[0] || cutItem.stockCount,
        rollCount: cutItem.rollCount[0],
        otherText: otherPlate
          ? `${otherPlate.width}*${otherPlate.height}`
          : null,
      }
      plankNum.push(cutItem?.stockCount?.[0] || (cutItem.stockCount as any))
      rollCount.push(cutItem.rollCount[0])
      stockArr.push(stock)
    })
    stockArr.sort((a, b) => {
      const lightKeyA = a.stockKey.includes('高光')
      const lightKeyB = b.stockKey.includes('高光')
      if (lightKeyA && !lightKeyB) {
        return -1
      } else if (!lightKeyA && lightKeyB) {
        return 1
      }
      const thickA = Number(a.stockKey.split(':').pop())
      const thickB = Number(b.stockKey.split(':').pop())
      return thickB - thickA
    })
    const overallPlankNum = plankNum.reduce((pre, cut) => {
      return (pre += cut)
    }, 0)
    const overallPlankRate = optimizeRate.length
      ? optimizeRate.reduce((pre, cut) => {
          return (pre += cut.rate)
        }, 0) / optimizeRate.length
      : 0
    Object.assign(newObj, {
      overallPlankNum,
      overallPlankRate,
      stock: stockArr,
      // 与页头 calcFanbanCount 同源
      rollAllCount: calcPlanFanbanCount(data, specialId),
    })
    result.push(newObj)
  })
  if (!result.length) {
    return result
  }
  // 找出利用率最高的一项（仅打标，不重排；顺序以 resultPlansObj 为准）
  const maxItem = result.reduce((acc, cur) => {
    return (cur.overallPlankRate * 100).toFixed(2) >
      (acc.overallPlankRate * 100).toFixed(2)
      ? cur
      : acc
  })
  maxItem.isMaxRate = true
  // 深度计算固定第二位，与 dealPlanksForMatCode 的 splice(1,0) 一致
  const deepIdx = result.findIndex((item) => item.isDeepSearch)
  if (deepIdx > -1 && deepIdx !== 1) {
    const [deepPlan] = result.splice(deepIdx, 1)
    result.splice(Math.min(1, result.length), 0, deepPlan)
  }
  return result
}

// 当前 tab 下可展示的方案列表
const currentTabPlans = computed<ICollectType[]>(() => {
  const matKey = activeMatKey.value
  if (!matKey) {
    return []
  }
  if (!matKeyList.value.length && store.state.ncSetting.glass_setting) {
    return []
  }
  const plans = resultPlansObjMap.value[matKey] || []
  return transformPlans(matKey, plans)
})

function handleTabChange(key: string) {
  activeMatKey.value = key
}

/**
 * 生成各材质激活方案组合后的缓存 key
 */
function getCombinedCacheKey(usingMap: Record<string, string | number>) {
  return Object.keys(usingMap)
    .sort()
    .map((key) => usingMap[key])
    .join('||')
}

/**
 * 按各材质当前激活的方案，从 resultPlansObj 组合成完整原始 paibanData。
 * 统一数据源，避免与首次组合排版结果混用导致数据异常。
 */
function getCombinedPaibanData(usingMap: Record<string, string | number>) {
  const collect = store.state.paibanStore?.resultPlansObj
  if (!collect || typeof collect !== 'object') {
    return null
  }
  const combined: Record<string, any> = {}
  let isGreatPlan = true
  let isDeepSearch = false
  let hasPlan = false

  Object.keys(collect).forEach((matKey) => {
    const plans = collect[matKey]
    if (!Array.isArray(plans) || !plans.length) {
      return
    }
    const defaultIdx = getDefaultPlanIndex(plans)
    const activeId =
      usingMap[matKey] ?? plans[defaultIdx]?.specialId ?? `${matKey}__0`
    const plan =
      plans.find((item: any, idx: number) => {
        const id = item.specialId ?? `${matKey}__${idx}`
        return String(id) === String(activeId)
      }) ||
      plans[defaultIdx] ||
      plans[0]
    if (!plan) {
      return
    }
    hasPlan = true
    const data = plan.data ?? plan
    // 只取材质对应的排版块，保证结构为 { data, used_rate, stockCount, ... }
    if (data[matKey] && data[matKey].data) {
      combined[matKey] = dataCopy(data[matKey])
    } else {
      Object.keys(data).forEach((key) => {
        const cutItem = data[key]
        if (
          cutItem &&
          typeof cutItem === 'object' &&
          Array.isArray(cutItem.data)
        ) {
          combined[key] = dataCopy(cutItem)
        }
      })
    }
    if (!(plan.isGreatPlan ?? data.isGreatPlan)) {
      isGreatPlan = false
    }
    if (plan.isDeepSearch ?? data.isDeepSearch) {
      isDeepSearch = true
    }
  })

  if (!hasPlan || !Object.keys(combined).length) {
    return null
  }

  Object.defineProperty(combined, 'isGreatPlan', {
    value: isGreatPlan,
    enumerable: false,
    configurable: true,
  })
  Object.defineProperty(combined, 'isDeepSearch', {
    value: isDeepSearch,
    enumerable: false,
    configurable: true,
  })
  return combined
}

/** 无激活记录时默认方案下标：非 QT 取 0；QT 有权限取深度方案；否则取最靠前非深度 */
function getDefaultPlanIndex(plans: any[]) {
  if (!plans?.length) {
    return 0
  }
  const types = ['paiban_v2', 'paiban_roll_v2']
  const isWebQT = !!(
    window.webToQt &&
    Reflect.has(window.webToQt, 'layout') &&
    types.includes(store.state.activePaibanWay[0])
  )
  if (!isWebQT) {
    return 0
  }
  const hasDeepSearchPermission =
    checkUserPermissions([USER_PERMISSION_KEY.DEEP_SEARCH]) === true
  if (hasDeepSearchPermission) {
    const deepIdx = plans.findIndex((plan: any) => isPlanDeepSearch(plan))
    return deepIdx > -1 ? deepIdx : 0
  }
  const normalIdx = plans.findIndex((plan: any) => !isPlanDeepSearch(plan))
  return normalIdx > -1 ? normalIdx : 0
}

function getScatteredLayout() {
  const isPreLayout = store.state.ncSetting.isPreLayout
  return isPreLayout
    ? store.state.preLayoutSetting.setting_value.movePlankSetting
        ?.scattered_layout
    : store.state.ncSetting.movePlankSetting?.scattered_layout
}

// ---------------- 方案应用前的数据补齐（历史记录兼容） ----------------

/**
 * 历史记录里的方案小板只保留了 id / index / 排布信息，
 * 而 afterProcess -> comparePart 是严格按 `dstPart.id === srcPart.partUniqueId`
 * 去 prePaibanData 里取整块板件数据的。
 * 一旦匹配不上，dstPart.rect 不会被赋值，后面 `dstPart.rect.width` 直接抛错，
 * 导致 paibanDataToView 中断、setPaibanData 不执行，表现就是“应用方案后数据是空的”。
 * 这里在应用前把计划板件与预处理数据对齐（补齐 id，必要时补 rect）。
 */
function getPrePaibanParts(): any[] {
  const rootList = store.state.prePaibanData
  const nsList = store.state.paibanStore?.prePaibanData
  const list = Array.isArray(rootList) && rootList.length ? rootList : nsList
  const arr = Array.isArray(list) ? list : Object.values(list || {})
  return arr
    .flat(Infinity)
    .filter((p: any) => p && typeof p === 'object' && !Array.isArray(p))
}

/** 序号统一成纯数字字符串，兼容预处理数据的 '01' 与方案数据的 1 */
function normalizeIndexKey(index: any, width = 0) {
  const num = Number(index)
  if (Number.isNaN(num)) return String(index ?? '')
  const str = String(num)
  return width > str.length ? str.padStart(width, '0') : str
}

function getIndexKey(head: string, index: any, width: number) {
  return `${head}:${normalizeIndexKey(index, width)}`
}

/** 计划板件与预处理板件的宽松匹配：唯一 id 优先，其次 颜色:材质:厚度:板号:序号 */
function lookupPrePart(
  part: any,
  cutKey: string,
  preById: Map<string, any>,
  preByIndex: Map<string, any>,
  indexWidth: number
) {
  // comparePart 契约：dstPart.id === srcPart.partUniqueId，优先用方案的唯一 id。
  // qty>1 时 partUniqueId 可能是共享 ori，仅当与 id 相同（或 id 缺失）时才采用。
  if (part.id != null && part.id !== undefined) {
    const hitById = preById.get(String(part.id))
    if (hitById) return hitById
  }
  if (
    part.partUniqueId != null &&
    part.partUniqueId !== undefined &&
    (part.id == null ||
      part.id === undefined ||
      String(part.partUniqueId) === String(part.id))
  ) {
    const hit = preById.get(String(part.partUniqueId))
    if (hit) return hit
  }
  if (part.index === undefined || part.index === null || part.index === '') {
    return null
  }
  const keyArr = String(cutKey).split(':')
  const head = `${keyArr[0] ?? ''}:${keyArr[1] ?? ''}:${keyArr[2] ?? ''}:${
    part.stockNum
  }`
  const candidates = [
    `${head}:${part.index}`,
    getIndexKey(head, part.index, 0),
    getIndexKey(head, part.index, indexWidth),
  ]
  for (const key of candidates) {
    const hit = preByIndex.get(key)
    if (hit) return hit
  }
  return null
}

/** 用预处理数据建立索引：唯一 id 索引 + 颜色:材质:厚度:板号:序号 索引 */
function buildPrePartIndex(preList: any[]) {
  const preById = new Map<string, any>()
  const preByIndex = new Map<string, any>()
  // 预处理数据的序号可能是 '01' 这类补零字符串，按最长宽度归一化，兼容数字写法
  const indexWidth = preList.reduce((max: number, pre: any) => {
    const len = String(pre?.index ?? '').length
    return len > max ? len : max
  }, 0)
  preList.forEach((pre: any) => {
    if (!pre || typeof pre !== 'object') return
    ;[pre.partUniqueId].forEach((id: any) => {
      if (id !== undefined && id !== null) preById.set(String(id), pre)
    })
    if (
      pre.index === undefined ||
      pre.index === null ||
      pre.index === '' ||
      pre.stockNum === undefined
    ) {
      return
    }
    const head = `${pre.texture}:${pre.matCode}:${pre.thick}:${pre.stockNum}`
    preByIndex.set(`${head}:${pre.index}`, pre)
    preByIndex.set(getIndexKey(head, pre.index, 0), pre)
    preByIndex.set(getIndexKey(head, pre.index, indexWidth), pre)
  })
  return { preById, preByIndex, indexWidth }
}

/** 只补齐缺失字段，不覆盖方案自身的数据 */
function supplementPlanPart(
  part: any,
  cutKey: string,
  preById: Map<string, any>,
  preByIndex: Map<string, any>,
  indexWidth: number
) {
  if (!part || typeof part !== 'object') return false
  const target = lookupPrePart(part, cutKey, preById, preByIndex, indexWidth)
  if (!target) return false

  let changed = false
  const uniqueId = target.partUniqueId ?? target.id
  if (
    uniqueId !== undefined &&
    uniqueId !== null &&
    String(part.id ?? '') !== String(uniqueId)
  ) {
    part.id = uniqueId
    changed = true
  }
  // 计划数据可能只有 rect.x1/y1/x2/y2，补一份带 width/height 的 rect
  if (
    part.rect &&
    part.rect.width === undefined &&
    part.rect.x1 !== undefined
  ) {
    part.rect.width = Math.abs(part.rect.x2 - part.rect.x1)
    part.rect.height = Math.abs(part.rect.y2 - part.rect.y1)
    changed = true
  }
  return changed
}

/**
 * 应用方案前对齐计划板件与 prePaibanData（历史记录方案数据只带 id/index）。
 * 就地补齐 collectItem.data，使其与实时排版结果结构一致。
 */
function supplementPlanDataForApply(planData: any) {
  if (!planData || typeof planData !== 'object') return planData
  const preList = getPrePaibanParts()
  if (!preList.length) return planData
  const { preById, preByIndex, indexWidth } = buildPrePartIndex(preList)
  Object.keys(planData).forEach((cutKey) => {
    const cutItem = planData[cutKey]
    const groups = cutItem?.data
    if (!Array.isArray(groups)) return
    groups.forEach((group: any) => {
      if (!Array.isArray(group)) return
      group.forEach((part: any) => {
        supplementPlanPart(part, cutKey, preById, preByIndex, indexWidth)
      })
    })
  })
  return planData
}

function expandPrePaibanSlots(prePaibanData: any[]) {
  const knifeMap = store.state.ncSetting.plate_knife_map
  const knifeMapKeys = Object.keys(knifeMap)
  const newKeysMap = new Map()
  knifeMapKeys.forEach((key) => {
    const [matCode, thick] = key.split(':')
    newKeysMap.set(`${matCode}:${Number(thick)}`, knifeMap[key])
  })
  prePaibanData.forEach((part: any) => {
    if (!part.specialType) {
      const stockKey = `${part.matCode}:${Number(part.thick)}`
      const selectKnife =
        newKeysMap.get(stockKey) ?? store.state.ncSetting.knife
      const cutKnifeR = part.isUseSaw
        ? getSawKnife(store.state.ncSetting) / 2
        : selectKnife.diameter / 2
      const expandObj = dealExpandSlotForPaiban(
        part,
        store.state.ncSetting,
        cutKnifeR
      )
      if (expandObj) {
        const expandedX = expandObj.left + expandObj.right
        const expandedY = expandObj.top + expandObj.bottom
        if (expandedX || expandedY) {
          part.expandRect = {
            x: part.rect.x,
            y: part.rect.y,
            width: part.rect.width + expandedX,
            height: part.rect.height + expandedY,
          }
        }
      }
    }
  })
}

/** 尊享：按材质切换方案后重组 */
function applyPaibanPlan(specialId: string | number) {
  const scattered_layout = getScatteredLayout()
  const paiban = currentTabPlans.value.find((item: any) => {
    return item.specialId === specialId
  })
  const matKey = (paiban as any)?.matKey || activeMatKey.value

  const nextUsingMap: Record<string, string | number> = {
    ...(store.state.paibanStore?.currentUsingSpecialIdMap || {}),
  }
  if (matKey) {
    nextUsingMap[matKey] = specialId
  }
  Object.keys(resultPlansObjMap.value).forEach((key) => {
    if (nextUsingMap[key] !== undefined) {
      return
    }
    const plans = resultPlansObjMap.value[key] || []
    const usePlan = plans[getDefaultPlanIndex(plans)]
    if (usePlan?.specialId) {
      nextUsingMap[key] = usePlan.specialId
    }
  })

  const combinedPaibanData = getCombinedPaibanData(nextUsingMap)
  if (!combinedPaibanData) {
    return modalMsg('出现错误，无法获取到数据！', 'error', '错误')
  }
  // 历史记录的方案板件只带 id/index，需先与预处理数据对齐，否则应用后数据为空
  supplementPlanDataForApply(combinedPaibanData)
  const hasSmallRect = !!(combinedPaibanData as any).isGreatPlan
  const cacheKey = getCombinedCacheKey(nextUsingMap)

  store.commit('awaitPaibanStore/clearLocalPlankStore')

  const loadingInstance = Loading.service({
    lock: true,
    text: '正在应用方案...',
    spinner: 'el-icon-loading',
    background: 'rgba(0, 0, 0, 0.7)',
  })

  function setCurrentUsing() {
    store.commit('paibanStore/setCurrentUsingSpecialId', specialId)
    store.commit('paibanStore/setCurrentUsingSpecialIdMap', nextUsingMap)
  }

  function setCurrentRawPlan() {
    store.commit(
      'paibanStore/setCurrentRawPlanData',
      dataCopy(combinedPaibanData)
    )
  }

  if (Reflect.has(store.state.paibanStore?.alreadyDealDrawData, cacheKey)) {
    const result = store.state.paibanStore?.alreadyDealDrawData[cacheKey]
    if (!result.paibanData) {
      return Message({
        type: 'warning',
        message: '请稍等,此方案数据正在预载中...',
        center: true,
      })
    }
    store.commit('setPaibanData', dataCopy(result.paibanData))
    setCurrentUsing()
    dealMatKeyForCloseList(nextUsingMap)
    if (result.rawPlanData) {
      store.commit(
        'paibanStore/setCurrentRawPlanData',
        dataCopy(result.rawPlanData)
      )
    } else {
      setCurrentRawPlan()
    }
    emit('useApply', cacheKey, result, loadingInstance)
    if (scattered_layout && !hasSmallRect) {
      store.commit('setIsShowDisPaiabnConfirm', true)
      emit('setAlignedPlanDialog', false)
    }
    return
  }

  setCurrentUsing()
  setCurrentRawPlan()
  dealMatKeyForCloseList(nextUsingMap)
  const prePaibanData = JSON.parse(JSON.stringify(store.state.prePaibanData))

  try {
    expandPrePaibanSlots(prePaibanData)
  } catch (error: any) {
    loadingInstance?.close()
    Message({
      type: 'error',
      message: error.message || '槽扩板计算失败，请在开料清单页面重新排版',
      center: true,
    })
    return
  }

  paibanDataToView(
    prePaibanData,
    JSON.parse(JSON.stringify(combinedPaibanData)),
    true,
    false
  ).then(() => {
    emit('useApply', cacheKey, false, loadingInstance)
    if (scattered_layout && !hasSmallRect) {
      store.commit('setIsShowDisPaiabnConfirm', true)
      emit('setAlignedPlanDialog', false)
    }
  })
}

/** 非尊享：整套组合方案切换 */
function applyCombinedPaibanPlan(specialId: string | number) {
  const scattered_layout = getScatteredLayout()
  const collectItem = store.state.paibanStore?.paibanDataCollect?.find(
    (it: any) => String(it.specialId) === String(specialId)
  )
  if (!collectItem?.data) {
    return modalMsg('出现错误，无法获取到数据！', 'error', '错误')
  }
  const hasSmallRect = !!(
    collectItem.isGreatPlan ?? collectItem.data?.isGreatPlan
  )
  const paibanData = collectItem.data
  // 历史记录的方案板件只带 id/index，需先与预处理数据对齐，否则应用后数据为空
  supplementPlanDataForApply(paibanData)

  store.commit('awaitPaibanStore/clearLocalPlankStore')

  const loadingInstance = Loading.service({
    lock: true,
    text: '正在应用方案...',
    spinner: 'el-icon-loading',
    background: 'rgba(0, 0, 0, 0.7)',
  })

  if (Reflect.has(store.state.paibanStore?.alreadyDealDrawData, specialId)) {
    const result = store.state.paibanStore?.alreadyDealDrawData[specialId]
    if (!result?.paibanData) {
      return Message({
        type: 'warning',
        message: '请稍等,此方案数据正在预载中...',
        center: true,
      })
    }
    store.commit('setPaibanData', dataCopy(result.paibanData))
    store.commit('paibanStore/setCurrentUsingSpecialId', specialId)
    store.commit('paibanStore/setCurrentUsingSpecialIdMap', {})
    if (result.rawPlanData) {
      store.commit(
        'paibanStore/setCurrentRawPlanData',
        dataCopy(result.rawPlanData)
      )
    } else {
      store.commit('paibanStore/setCurrentRawPlanData', dataCopy(paibanData))
    }
    emit('useApply', specialId, result, loadingInstance)
    if (scattered_layout && !hasSmallRect) {
      store.commit('setIsShowDisPaiabnConfirm', true)
      emit('setAlignedPlanDialog', false)
    }
    return
  }

  store.commit('paibanStore/setCurrentUsingSpecialId', specialId)
  store.commit('paibanStore/setCurrentUsingSpecialIdMap', {})
  store.commit('paibanStore/setCurrentRawPlanData', dataCopy(paibanData))
  const prePaibanData = JSON.parse(JSON.stringify(store.state.prePaibanData))

  try {
    expandPrePaibanSlots(prePaibanData)
  } catch (error: any) {
    loadingInstance?.close()
    Message({
      type: 'error',
      message: error.message || '槽扩板计算失败，请在开料清单页面重新排版',
      center: true,
    })
    return
  }

  paibanDataToView(
    prePaibanData,
    JSON.parse(JSON.stringify(paibanData)),
    true,
    false
  ).then(() => {
    emit('useApply', specialId, false, loadingInstance)
    if (scattered_layout && !hasSmallRect) {
      store.commit('setIsShowDisPaiabnConfirm', true)
      emit('setAlignedPlanDialog', false)
    }
  })
}

function dataCopy(data: any) {
  return JSON.parse(JSON.stringify(data))
}

/**
 * @description 更多结果stockKey的高光显示
 * */
function dealStockKeyName(name: string) {
  try {
    const arrs = name.split(':')
    // 颜色:材质:厚度
    if (arrs[1].startsWith('高光_')) {
      arrs[1] = arrs[1].replace(
        '高光_',
        `${matchHighlightBoardName(arrs[1], false)}_`
      )
    }
    return arrs.join(':')
  } catch (err) {
    console.log(err, 'err')
    return name
  }
}

return { __sfc: true,emit, props, isExpressVip, currentLine, getBigPlankParts, isDesMaterialNoCutBigPlank, countFanbanLikeActivate, getPrePartMap, enrichPartForFanban, extractBigPlanksFromCutItem, calcPlanFanbanCount, resultPlansObjMap, matKeyList, combinedPlanList, activeMatKey, closeMatKeyList, deepTabTipTitle, isPlanDeepSearch, hasBetterDeepSearchPlans, isDeepTabTipVisible, handleDeepTabTipVisibleChange, handleCloseDeepTabTip, handleOpenDeepTabTip, getDeepTabTipShowCount, emitDeepTabTipShowState, dealMatKeyForCloseList, getComponentId, cutUsePlan, cutUsePlanMap, isCurrentUsingPlan, transformPlans, currentTabPlans, handleTabChange, getCombinedCacheKey, getCombinedPaibanData, getDefaultPlanIndex, getScatteredLayout, getPrePaibanParts, normalizeIndexKey, getIndexKey, lookupPrePart, buildPrePartIndex, supplementPlanPart, supplementPlanDataForApply, expandPrePaibanSlots, applyPaibanPlan, applyCombinedPaibanPlan, dataCopy, dealStockKeyName, checkUserPermissions, USER_PERMISSION_KEY, MDeepBadgeAurora }
}

})